import React, { useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreatePreparationDocumentDto,
  PreparationDocumentDto,
  CompoundDto,
  UpdatePreparationDocumentDto,
} from '@services/dtos'
import { getPreparationDocumentSchema } from '@utils/schemas'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { Grid, Typography } from '@mui/material'
import {
  createPreparationDocument,
  updatePreparationDocument,
} from '@state/reducers/preparationDocumentReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormCrudHeader } from './FormCrudHeader'
import InputField from '../inputs/InputField'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import ServerAutocomplete from '../autocomplete/ServerAutocomplete'
import SelectField from '../inputs/SelectField'
import { Site } from '@/services/api'
import { compoundService } from '@services/CompoundService'
import CompoundForm from './CompoundForm'
import { FilePreview } from '../file/FilePreview'
import { SimpleFileInput } from '../inputs/SimpleFileInput'

type Props = {
  editingPreparationDocument?: PreparationDocumentDto | null
  onClose: () => void
}

const PreparationDocumentForm: React.FC<Props> = ({
  editingPreparationDocument,
  onClose,
}) => {
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
    watch,
  } = useForm<CreatePreparationDocumentDto>({
    defaultValues: editingPreparationDocument
      ? {
          ...editingPreparationDocument,
          siteId: editingPreparationDocument.site.id,
          compoundId: editingPreparationDocument.compound.id,
        }
      : {},
    resolver: yupResolver(getPreparationDocumentSchema(!!editingPreparationDocument)),
  })

  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreatePreparationDocumentDto) => {
    setIsSubmitting(true)
    const displayErrorMessage = () => cb('Une erreur est survenue')

    const formData = new FormData()
    formData.append('compoundId', data.compoundId)
    formData.append('siteId', data.siteId.toString())
    formData.append('label', data.label)
    if (data.file) formData.append('file', data.file)

    if (editingPreparationDocument) {
      dispatch(
        updatePreparationDocument({
          id: editingPreparationDocument.id,
          dto: formData as unknown as UpdatePreparationDocumentDto,
        }),
      )
        .unwrap()
        .then(() => {
          cb('Le document a été modifié avec succès')
          onClose()
        })
        .catch(() => displayErrorMessage())
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(
        createPreparationDocument(
          formData as unknown as CreatePreparationDocumentDto,
        ),
      )
        .unwrap()
        .then(() => {
          cb('Le document a été créé avec succès')
          onClose()
        })
        .catch(() => displayErrorMessage())
        .finally(() => setIsSubmitting(false))
    }
  }

  const fields: { name: 'label'; label: string; type?: string; sm?: number }[] =
    [{ name: 'label', label: 'Libellé', sm: 6, type: 'text' }]
  const renderFields = (
    fields: { name: 'label'; label: string; type?: string; sm?: number }[],
  ) => {
    return fields.map((field) => (
      <Grid item xs={12} sm={field.sm ?? 6} key={field.name}>
        <InputField
          control={control}
          name={field.name}
          label={field.label}
          type={field.type ?? 'text'}
          multiline={field.type === 'textarea'}
          rows={field.type === 'textarea' ? 2 : undefined}
          helperText={errors[field.name]?.message}
          error={!!errors[field.name]}
        />
      </Grid>
    ))
  }

  const fetchCompounds = async (search: string) => {
    const page = await compoundService.getAll({ search })
    const list = page?.datas ?? []
    return list
  }

  const sites = useMemo(() => (user as any & { sites: Site[] })?.sites, [user])
  const file = watch('file')

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingPreparationDocument}
        />

        <div className="w-full mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Document
            </legend>

            <Grid container columnSpacing={2} mt={1}>
              <Grid
                container
                item
                xs={12}
                md={5}
                spacing={2}
                alignContent="flex-start"
              >
                {renderFields(fields)}

                <Grid item xs={12} sm={6}>
                  {sites?.length ? (
                    <SelectField
                      control={control}
                      name="siteId"
                      options={sites.map(({ id, label }) => ({
                        label,
                        value: id,
                      }))}
                      value={watch('siteId')}
                      label="Site"
                    />
                  ) : (
                    <Typography variant="body1">
                      Aucun site enregistré
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <AddWrapperInput
                    legend="MRP"
                    addTooltipTitle="Ajouter un MRP"
                    dialogContent={(close) => (
                      <CompoundForm onClose={close} />
                    )}
                    input={
                      <ServerAutocomplete<CompoundDto>
                        fetchService={fetchCompounds}
                        onChange={(value: CompoundDto | null) => {
                          setValue('compoundId', value?.id ?? '')
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const compound = value as CompoundDto
                          return compound ? compound.name : ''
                        }}
                        debounceMs={300}
                        placeholder="Rechercher un MRP"
                        label="MRP"
                        helperText={errors.compoundId?.message}
                        error={!!errors.compoundId}
                        minSearchLength={0}
                        initialOptions={
                          editingPreparationDocument?.compound
                            ? [editingPreparationDocument.compound]
                            : []
                        }
                        value={editingPreparationDocument?.compound ?? null}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <SimpleFileInput
                    file={file}
                    onSelect={(file) => setValue('file', file)}
                    error={errors.file?.message}
                  />
                </Grid>
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={7}
                sx={{
                  minHeight: 300,
                  height: '65vh',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FilePreview file={file} attachment={editingPreparationDocument?.attachment} />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default PreparationDocumentForm
